import React, { useState, useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
} from '@tanstack/react-table';
import { ArrowUp, ArrowDown, ChevronsUpDown, ChevronLeft, ChevronRight, Inbox } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import EmptyState from './EmptyState';

/**
 * <DataTable /> — universal governance table.
 *
 * Built on @tanstack/react-table v8. One component replaces 5 hand-rolled
 * tables (Approvals/Budgets/Confidence/Audit/Tenants).
 *
 * Features:
 *   - Sort (click header)
 *   - Pagination
 *   - Row selection w/ bulk-action bar
 *   - Per-row onClick (drill-in)
 *   - Sticky header
 *   - Empty + loading states
 *   - Compact density toggle (planned future)
 *
 * Props:
 *   columns: tanstack column defs
 *   data:    row data
 *   loading: bool
 *   bulkActions: (rows) => ReactNode    optional
 *   onRowClick: (row) => void           optional
 *   emptyTitle / emptyHint               optional
 *   testId:  data-testid prefix
 */
export default function DataTable({
  columns,
  data,
  loading = false,
  bulkActions,
  onRowClick,
  emptyTitle = 'Nothing here yet',
  emptyHint,
  emptyIcon = Inbox,
  pageSize = 25,
  testId = 'gov-data-table',
}) {
  const [sorting, setSorting] = useState([]);
  const [rowSelection, setRowSelection] = useState({});
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize });

  const mergedColumns = useMemo(() => {
    if (!bulkActions) return columns;
    // Prepend a checkbox column
    return [
      {
        id: '_select',
        header: ({ table }) => (
          <input
            type="checkbox"
            checked={table.getIsAllRowsSelected()}
            ref={(el) => { if (el) el.indeterminate = table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected(); }}
            onChange={table.getToggleAllRowsSelectedHandler()}
            className="h-3.5 w-3.5 rounded border-[var(--gov-border-strong)] bg-transparent"
            data-testid="gov-table-select-all"
          />
        ),
        cell: ({ row }) => (
          <input
            type="checkbox"
            checked={row.getIsSelected()}
            onChange={row.getToggleSelectedHandler()}
            onClick={(e) => e.stopPropagation()}
            className="h-3.5 w-3.5 rounded border-[var(--gov-border-strong)] bg-transparent"
            data-testid={`gov-table-row-select-${row.id}`}
          />
        ),
        enableSorting: false,
        size: 32,
      },
      ...columns,
    ];
  }, [columns, bulkActions]);

  const table = useReactTable({
    data: data || [],
    columns: mergedColumns,
    state: { sorting, rowSelection, pagination },
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    onPaginationChange: setPagination,
    enableRowSelection: !!bulkActions,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const selectedRows = table.getSelectedRowModel().rows.map((r) => r.original);

  if (loading) {
    return (
      <div className="gov-card overflow-hidden" data-testid={`${testId}-loading`}>
        <div className="gov-skeleton h-10 w-full" />
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="gov-skeleton mt-px h-12 w-full" />
        ))}
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="gov-card p-8" data-testid={`${testId}-empty`}>
        <EmptyState icon={emptyIcon} title={emptyTitle} hint={emptyHint} />
      </div>
    );
  }

  return (
    <div className="gov-card overflow-hidden" data-testid={testId}>
      {/* Bulk action bar — appears when rows selected */}
      {bulkActions && selectedRows.length > 0 && (
        <div className="flex items-center gap-3 border-b border-[var(--gov-border)] bg-[var(--gov-accent-bg)] px-4 py-2.5 text-sm">
          <span className="gov-text-primary font-medium">{selectedRows.length} selected</span>
          <span className="gov-text-tertiary">•</span>
          <div className="flex items-center gap-2">{bulkActions(selectedRows, () => setRowSelection({}))}</div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid={`${testId}-table`}>
          <thead className="bg-[var(--gov-bg-elevated)] sticky top-0 z-10">
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id} className="border-b border-[var(--gov-border)]">
                {hg.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const sorted = header.column.getIsSorted();
                  return (
                    <th
                      key={header.id}
                      style={{ width: header.column.columnDef.size }}
                      onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                      className={`gov-label px-4 py-2.5 text-left ${canSort ? 'cursor-pointer select-none hover:text-[var(--gov-text-primary)]' : ''}`}
                    >
                      <span className="inline-flex items-center gap-1.5">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {canSort && (
                          sorted === 'asc' ? <ArrowUp className="h-3 w-3" /> :
                          sorted === 'desc' ? <ArrowDown className="h-3 w-3" /> :
                          <ChevronsUpDown className="h-3 w-3 opacity-40" />
                        )}
                      </span>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                className={`border-b border-[var(--gov-border)] last:border-0 gov-row-hover ${onRowClick ? 'cursor-pointer' : ''} ${row.getIsSelected() ? 'bg-[var(--gov-accent-bg)]' : ''}`}
                data-testid={`${testId}-row-${row.id}`}
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="gov-text-primary px-4 py-3 align-middle">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer / pagination */}
      <div className="flex items-center justify-between border-t border-[var(--gov-border)] px-4 py-2.5 text-xs">
        <span className="gov-text-tertiary">
          Showing {table.getRowModel().rows.length} of {data.length}
        </span>
        {table.getPageCount() > 1 && (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost" size="sm" className="h-7 px-2"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              data-testid={`${testId}-prev`}
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            <span className="gov-text-secondary gov-tabular px-2">
              Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
            </span>
            <Button
              variant="ghost" size="sm" className="h-7 px-2"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              data-testid={`${testId}-next`}
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
